import { bookingRecordAssetId } from './bookingListDisplay';
import { bookingRecordIsTerminal } from './bookingRecordMutate';
import {
  collectReservedDayKeysForAsset,
  isPastCalendarDayKey,
  parseCalendarDayKey,
} from './assetBookingCalendar';
import {
  bufferedStartReferenceNow,
  isLocalCalendarDayToday,
} from './bookingScheduleDefaults';
import {
  calendarDayIsClosed,
  openingHourEndOptionsForDay,
  openingHourStartOptionsForDay,
} from './openingHoursBookingValidate';

type MinuteRange = { startMin: number; endMin: number };

function parseHmToMinutes(hm: string): number | null {
  const match = /^(\d{2}):(\d{2})$/.exec(hm.trim());
  if (!match) {
    return null;
  }
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (!Number.isFinite(hour) || !Number.isFinite(minute) || hour < 0 || hour > 23) {
    return null;
  }
  return hour * 60 + minute;
}

function rangesOverlap(a: MinuteRange, b: MinuteRange): boolean {
  return a.startMin < b.endMin && b.startMin < a.endMin;
}

function bookingIntervalOnDay(booking: Record<string, unknown>, dayKey: string): MinuteRange | null {
  const startRaw = booking.start_date;
  const endRaw = booking.end_date;
  if (typeof startRaw !== 'string' || typeof endRaw !== 'string') {
    return null;
  }
  const start = new Date(startRaw.trim());
  const end = new Date(endRaw.trim());
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end.getTime() <= start.getTime()) {
    return null;
  }
  const parsed = parseCalendarDayKey(dayKey);
  if (!parsed) {
    return null;
  }
  const dayStart = new Date(parsed.year, parsed.monthIndex, parsed.day, 0, 0, 0, 0);
  const dayEnd = new Date(parsed.year, parsed.monthIndex, parsed.day, 23, 59, 59, 999);
  if (end.getTime() <= dayStart.getTime() || start.getTime() >= dayEnd.getTime()) {
    return null;
  }
  const clipStart = new Date(Math.max(start.getTime(), dayStart.getTime()));
  const clipEnd = new Date(Math.min(end.getTime(), dayEnd.getTime()));
  return {
    startMin: clipStart.getHours() * 60 + clipStart.getMinutes(),
    endMin: clipEnd.getHours() * 60 + clipEnd.getMinutes(),
  };
}

function activeBookingIntervalsOnDay(
  bookings: Record<string, unknown>[],
  assetId: string,
  dayKey: string,
): MinuteRange[] {
  const want = assetId.trim().toLowerCase();
  if (!want) {
    return [];
  }
  const out: MinuteRange[] = [];
  for (const booking of bookings) {
    if (bookingRecordIsTerminal(booking)) {
      continue;
    }
    const bid = bookingRecordAssetId(booking);
    if (!bid || bid.toLowerCase() !== want) {
      continue;
    }
    const interval = bookingIntervalOnDay(booking, dayKey);
    if (interval) {
      out.push(interval);
    }
  }
  return out;
}

function candidateRangeIsFree(range: MinuteRange, occupied: MinuteRange[]): boolean {
  return !occupied.some((block) => rangesOverlap(range, block));
}

function minStartMinuteOnToday(dayKey: string, now: Date = new Date()): number | null {
  const parsed = parseCalendarDayKey(dayKey);
  if (!parsed || !isLocalCalendarDayToday(parsed.year, parsed.monthIndex, parsed.day, now)) {
    return null;
  }
  const buffered = bufferedStartReferenceNow(now);
  const minute = buffered.getHours() * 60 + buffered.getMinutes();
  const rounded = Math.ceil(minute / 60) * 60;
  return rounded;
}

function hasBookableEndForStart(
  asset: Record<string, unknown>,
  dayKey: string,
  startHm: string,
  occupied: MinuteRange[],
): boolean {
  return openingHourEndOptionsForDay(asset, dayKey, startHm).some((endHm) => {
    const startMin = parseHmToMinutes(startHm);
    const endMin = parseHmToMinutes(endHm);
    if (startMin === null || endMin === null) {
      return false;
    }
    return candidateRangeIsFree({ startMin, endMin }, occupied);
  });
}

/** Days with no remaining hourly slot (asset open but fully reserved). */
export function collectHourlyFullyBookedDayKeys(
  asset: Record<string, unknown>,
  bookings: Record<string, unknown>[],
  assetId: string,
): Set<string> {
  const reserved = collectReservedDayKeysForAsset(bookings, assetId);
  const out = new Set<string>();
  reserved.forEach((dayKey) => {
    if (calendarDayIsClosed(asset, dayKey)) {
      return;
    }
    if (bookableHourStartOptions(asset, dayKey, bookings, assetId).length === 0) {
      out.add(dayKey);
    }
  });
  return out;
}

/** Daily bookings treat any reserved calendar day as fully booked. */
export function collectDailyFullyBookedDayKeys(
  bookings: Record<string, unknown>[],
  assetId: string,
): Set<string> {
  return collectReservedDayKeysForAsset(bookings, assetId);
}

export function bookableHourStartOptions(
  asset: Record<string, unknown>,
  dayKey: string,
  bookings: Record<string, unknown>[],
  assetId: string,
  now: Date = new Date(),
): string[] {
  if (calendarDayIsClosed(asset, dayKey)) {
    return [];
  }
  const occupied = activeBookingIntervalsOnDay(bookings, assetId, dayKey);
  const minToday = minStartMinuteOnToday(dayKey, now);
  return openingHourStartOptionsForDay(asset, dayKey).filter((startHm) => {
    const startMin = parseHmToMinutes(startHm);
    if (startMin === null) {
      return false;
    }
    if (minToday !== null && startMin < minToday) {
      return false;
    }
    return hasBookableEndForStart(asset, dayKey, startHm, occupied);
  });
}

export function bookableHourEndOptions(
  asset: Record<string, unknown>,
  dayKey: string,
  startHm: string,
  bookings: Record<string, unknown>[],
  assetId: string,
): string[] {
  if (calendarDayIsClosed(asset, dayKey)) {
    return [];
  }
  const startMin = parseHmToMinutes(startHm);
  if (startMin === null) {
    return [];
  }
  const occupied = activeBookingIntervalsOnDay(bookings, assetId, dayKey);
  return openingHourEndOptionsForDay(asset, dayKey, startHm).filter((endHm) => {
    const endMin = parseHmToMinutes(endHm);
    if (endMin === null) {
      return false;
    }
    return candidateRangeIsFree({ startMin, endMin }, occupied);
  });
}

export function isCalendarDaySelectable(
  asset: Record<string, unknown>,
  dayKey: string,
  mode: 'hour' | 'day' | 'month',
  bookings: Record<string, unknown>[],
  assetId: string,
): boolean {
  if (isPastCalendarDayKey(dayKey)) {
    return false;
  }
  if (calendarDayIsClosed(asset, dayKey)) {
    return false;
  }
  if (mode === 'day' || mode === 'month') {
    return !collectDailyFullyBookedDayKeys(bookings, assetId).has(dayKey);
  }
  return bookableHourStartOptions(asset, dayKey, bookings, assetId).length > 0;
}

/** Default start/end pair for a newly selected open day. */
export function defaultBookableHourRange(
  asset: Record<string, unknown>,
  dayKey: string,
  bookings: Record<string, unknown>[],
  assetId: string,
): { start: string; end: string } | null {
  const starts = bookableHourStartOptions(asset, dayKey, bookings, assetId);
  if (starts.length === 0) {
    return null;
  }
  const start = starts[0];
  const ends = bookableHourEndOptions(asset, dayKey, start, bookings, assetId);
  if (ends.length === 0) {
    return null;
  }
  return { start, end: ends[0] };
}
